import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/gear-storage-fee.jpg'),
    alt: 'gear storage fee page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'gear storage fee page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Affordable Gear <span>Storage Fees</span>',
  description:
    'At ShipAndStorage, whether you’re storing sports gear, outdoor equipment, or personal items, our gear storage fee ensures you only pay for what you store. ',
  image: {
    url: '/images/v2/pages/main-pages/gear-storage-fee/hero-image.jpg',
    alt: 'gear storage fee page'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Calculate Your Fee' }
}

export const whyWeSectionContent = {
  title: 'How Gear Storage Works at ShipAndStorage',
  description: 'Storing your gear with us is effortless. Follow these steps:',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Get Storage Today' },

  cards: [
    {
      id: 1,
      title: 'Sign Up',
      description: 'Create an account and explore storage options tailored to your needs.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/sign-up.svg',
        alt: 'Sign Up'
      }
    },
    {
      id: 2,
      title: 'Schedule Pickup or Drop-Off',
      description: 'Choose a convenient option to deliver your gear to our facility.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/schedule-pickup-or-drop-off.svg',
        alt: 'Schedule Pickup or Drop-Off'
      }
    },
    {
      id: 3,
      title: 'Secure Storage',
      description: 'Your items are stored in climate-controlled and monitored environments for safety.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/secure-storage.svg',
        alt: 'Secure Storage'
      }
    },
    {
      id: 4,
      title: 'Retrieve Anytime',
      description: 'Access your gear anytime, with delivery options available to save time and effort.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/retrieve-anytime.svg',
        alt: 'Retrieve Anytime'
      }
    }
  ]
}

export const helperSectionContent = {
  title: 'Flexible and Transparent Sport Gear Storage Fees',

  articles: [
    {
      content: [
        {
          id: 1,
          description:
            'Gear Storage Fee is the cost associated with safely storing your sports gear, outdoor equipment, or belongings in a state-of-the-art facility. Unlike traditional storage solutions that charge for entire units, ShipAndStorage uses a pay-per-item model, which means you’re only billed for the items you store. This approach eliminates unnecessary expenses and offers unmatched cost efficiency.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/flexible-and-transparent-sport-gear-storage-fees.jpg',
        alt: 'Flexible and Transparent Sport Gear Storage Fees'
      }
    }
  ]
}

export const serviceTipsSectionContent = {
  title: 'How Much Does Gear Storage Cost at ShipAndStorage?',
  description: 'We’ve designed our pricing to be simple and budget-friendly:',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Get Customized Pricing' },

  cards: [
    {
      id: 1,
      title: 'Free Storage Period',
      description: '6 Months Free Storage: Enjoy complimentary storage for the first six months.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/free-storage-period.svg',
        alt: 'Free Storage Period'
      }
    },
    {
      id: 2,
      title: 'Extended Storage',
      description: '$10/month per item: Ideal for luggage, bags, small boxes, and similar items.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/extended-storage.svg',
        alt: 'Extended Storage'
      }
    },
    {
      id: 3,
      title: 'Additional Services',
      description: 'Pickup and Delivery: Starting at $15 per trip.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/additional-services.svg',
        alt: 'Additional Services'
      }
    }
  ]
}

export const whyChooseSectionContent = {
  title: 'Why Choose ShipAndStorage for Gear Storage?',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Pay-Per-Item Flexibility',
          points: [
            'Say goodbye to oversized storage units you don’t fully use.',
            'Perfect for individual items like skis, snowboards, golf clubs, bicycles, and more.'
          ]
        },
        {
          id: 2,
          title: 'No Hidden Fees',
          points: [
            'Complete transparency in pricing, with no surprise charges.',
            'Detailed billing ensures you know exactly what you’re paying for.'
          ]
        },
        {
          id: 3,
          title: 'Customizable Storage Plans',
          points: [
            'Tailored options for short-term or long-term storage.',
            'Flexible plans that adapt to your needs as they change.'
          ]
        },
        {
          id: 4,
          title: 'Secure and Accessible',
          points: [
            'Climate-controlled facilities to keep your gear in optimal condition.',
            '24/7 monitoring to safeguard your items.',
            'Convenient pickup and delivery options to save you time.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/why-choose-shipandstorage-for-gear-storage.jpg',
        alt: 'Why Choose ShipAndStorage for Gear Storage?'
      }
    }
  ]
}

export const tipsSectionContent = {
  title: 'Tips to Minimize Your Gear Storage Fee',

  cards: [
    {
      id: 1,
      title: 'Consolidate Your Gear',
      description:
        'Combine smaller items into fewer packages to save space and minimize the number of individually stored items, reducing your overall storage costs.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/consolidate-your-gear.svg',
        alt: 'Consolidate Your Gear'
      }
    },
    {
      id: 2,
      title: 'Choose Seasonal Storage',
      description:
        'Store your gear during off-seasons when you won’t need it, avoiding unnecessary year-round expenses and ensuring maximum utility from your storage plan.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/choose-seasonal-storage.svg',
        alt: 'Choose Seasonal Storage'
      }
    },
    {
      id: 3,
      title: 'Clean and Maintain Gear Before Storage',
      description:
        'Prepare your items by cleaning and inspecting them beforehand, preventing damage and reducing potential expenses on repairs or replacements when retrieving them.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/clean-and-maintain-gear-before-storage.svg',
        alt: 'Clean and Maintain Gear Before Storage'
      }
    },
    {
      id: 4,
      title: 'Plan Ahead',
      description:
        'Coordinate your storage schedule with your activity calendar to align with usage needs, ensuring you only pay for the time your gear is stored.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/plan-ahead.svg',
        alt: 'Plan Ahead'
      }
    },
    {
      id: 5,
      title: 'Optimize Storage Space',
      description:
        'Use appropriately sized boxes or containers to maximize space efficiency, ensuring you pay for the smallest storage unit required for your items.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/optimize-storage-space.svg',
        alt: 'Optimize Storage Space'
      }
    },
    {
      id: 6,
      title: 'Take Advantage of Promotions',
      description:
        'Watch for discounts, seasonal offers, or free storage promotions to lower your costs, especially during peak storage times or special occasions.',
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/take-advantage-of-promotions.svg',
        alt: 'Take Advantage of Promotions'
      }
    }
  ]
}

export const benefitsSectionContent = {
  title: 'Benefits of ShipAndStorage’s Gear Storage Fee Model',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Pay-Per-Item Flexibility',
          description:
            'Only pay for the items you store, avoiding unnecessary expenses associated with traditional unit rentals.'
        },
        {
          id: 2,
          title: 'No Hidden Fees',
          description:
            'Whether you need storage for weeks, months, or years, our plans accommodate your timeline without binding commitments.'
        },
        {
          id: 3,
          title: 'Customizable Storage Plans',
          description: 'Facilities designed to cater to specific needs, from sports gear to unique items.'
        },
        {
          id: 4,
          title: 'Secure and Accessible',
          description:
            'Effortless online management and flexible scheduling for pickups and deliveries make the process seamless.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/gear-storage-fee/benefits-of-shipandstorage-gear-storage-fee-model.jpg',
        alt: 'Benefits of ShipAndStorage’s Gear Storage Fee Model'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Affordable Gear Storage Made Simple',
  description:
    'Store your gear affordably with ShipAndStorage’s pay-per-item model. Enjoy secure, climate-controlled facilities, six months of free storage, and flexible plans that adapt to your needs. Get started today!',
  image: {
    src: '/images/v2/pages/main-pages/gear-storage-fee/promotional-banner-image.svg',
    alt: 'Affordable Gear Storage Made Simple'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Start Storing Now' }
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions About Gear Storage Fee',

  questions: [
    {
      id: 1,
      question: 'Are there discounts for multiple items?',
      answer: 'Yes! We offer bundled pricing for customers storing multiple items. Contact us for details.'
    },
    {
      id: 2,
      question: 'Can I change my storage plan?',
      answer:
        'Absolutely. Our plans are flexible, allowing you to add or remove items or adjust storage durations as needed.'
    },
    {
      id: 3,
      question: 'What happens if I don’t pay my fee?',
      answer:
        'A grace period is provided, along with reminders. Unpaid fees will be handled according to our terms and conditions.'
    },
    {
      id: 4,
      question: 'Do you provide insurance for stored gear?',
      answer: 'Yes, optional insurance coverage is available for added peace of mind.'
    },
    {
      id: 5,
      question: 'Can I track my stored items?',
      answer: 'Yes, our platform allows you to manage and track your stored items online anytime.'
    }
  ]
}
